import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useReducer,
  useCallback,
} from 'react'

import { Permissions, Subscription, TrialExhaust } from '../types'
import { useLocalStorage } from '../hooks/useLocalStorage'

type SidebarCountKeys =
  | 'allProjectsCount'
  | 'keywordPortfoliosCount'
  | 'allAssignmentsCount'

type State = {
  topbarComponent: ReactNode | null
  opportunityBoardComponent: ReactNode | null
  navbarComponent: ReactNode | null
  inboxThreadUpdate: boolean
  assignmentRefetchFunction: (() => void) | null
  isRefetchBoolean: boolean
  viewport: 'sm' | 'md' | 'lg'
  sidebarCounts: {
    allProjectsCount: number
    keywordPortfoliosCount: number
    allAssignmentsCount: number
  }
  pendingSidebarCountKeys: SidebarCountKeys[]
  pepperEditorType: PepperDocTypes | null
  isPepperDocEditable: boolean
  docErrorUpdate: boolean
  editorUpdateStatus: EditorUpdateStatusType
  privateItemDetails: {
    title: string
    code: number
    is_renamed: boolean
  } | null
  assignmentDetails: Assignment | null
  shouldClearTimeout: boolean
  shouldShowReworkRejectMenu: boolean
  creatorUserRole: CreatorRole | null
  editorNetworkState: NetworkStatusType
  permissions: Permissions | null
  trialExhaust: TrialExhaust | null
  subscription: Subscription | null
  bulkDownloadJob: BulkDownloadState | null
  domainId?: string
}

type BulkDownloadState = {
  id: string
  name: string
  companyId: string
  count: number
  status: 'pending' | 'success'
}

type TourContextType = {
  setPendingSidebarCountKeys: (
    value: SidebarCountKeys[],
    type?: 'push' | 'remove'
  ) => void
  setTopbarComponent: (value: ReactNode | null) => void
  setOpportunityBoardComponent: (value: ReactNode | null) => void
  setNavbarComponent: (value: ReactNode | null) => void
  setInboxThreadUpdate: (value: boolean) => void
  setAssignmentRefetchFunction: (fn: () => void) => void
  setRefetchBoolean: (value: boolean) => void
  setViewport: (value: string) => void
  setSidebarCountData: (val: number, key: SidebarCountKeys) => void
  setPepperEditorType: (val: PepperDocTypes | null) => void
  setIsPepperDocEditable: (val: boolean) => void
  setDocErrorUpdate: (val: boolean) => void
  setPrivateItemDetails: (
    val: { title: string; code: number; is_renamed: boolean } | null
  ) => void
  setEditorUpdateStatus: (val: EditorUpdateStatusType) => void
  setAssignmentDetails: (val: Assignment | null) => void
  setShouldClearTimeout: (val: boolean) => void
  setShouldShowReworkRejectMenu: (val: boolean) => void
  setCreatorUserRole: (val: CreatorRole | null) => void
  setEditorNetworkState: (val: NetworkStatusType) => void
  setPermissions: (val: Permissions) => void
  setTrialExhaust: (val: TrialExhaust) => void
  setSubscription: (val: Subscription) => void
  setBulkDownloadJob: (val: BulkDownloadState | null) => void
  setDomainId: (val: string) => void
} & State

type ReducerType =
  | 'SET_PENDING_SIDEBAR_DATA_KEYS'
  | 'SET_TOP_BAR_COMPONENT'
  | 'SET_OPPORTUNITY_BOARD_COMPONENT'
  | 'SET_NAV_BAR_COMPONENT'
  | 'SET_INBOX_UNREAD_UPDATE'
  | 'SET_ASSIGNMENT_REFETCH_FUNCTION'
  | 'SET_STREAM_CHAT'
  | 'SET_REFETCH_BOOLEAN'
  | 'SET_VIEWPORT'
  | 'SET_SIDEBAR_COUNT_DATA'
  | 'SET_IS_PEPPER_EDITOR'
  | 'SET_IS_PEPPER_DOC_EDITABLE'
  | 'SET_NETWORK_STATUS'
  | 'SET_DOC_ERROR_UPDATE'
  | 'SET_DOC_UPDATING'
  | 'SET_ITEM_DETAILS'
  | 'SET_EDITOR_UPDATE_STATUS'
  | 'SET_ASSIGNMENT_DETAILS'
  | 'SET_SHOULD_CLEAR_TIMEOUT'
  | 'SET_SHOW_REWORK_REJECT'
  | 'SET_CREATOR_USER_ROLE'
  | 'SET_EDITOR_NETWORK_STATE'
  | 'SET_PERMISSIONS'
  | 'SET_TRIAL_EXHAUST'
  | 'SET_SUBSCRIPTION'
  | 'SET_DOMAIN_ID'

const initialState: State = {
  topbarComponent: null,
  opportunityBoardComponent: null,
  navbarComponent: null,
  inboxThreadUpdate: true,
  assignmentRefetchFunction: null,
  isRefetchBoolean: false,
  viewport: 'lg',
  sidebarCounts: {
    allProjectsCount: 0,
    keywordPortfoliosCount: 0,
    allAssignmentsCount: 0,
  },
  pendingSidebarCountKeys: ['allProjectsCount', 'keywordPortfoliosCount'],
  pepperEditorType: null,
  isPepperDocEditable: true,
  docErrorUpdate: false,
  privateItemDetails: null,
  editorUpdateStatus: 'UPDATED',
  assignmentDetails: null,
  shouldClearTimeout: false,
  shouldShowReworkRejectMenu: false,
  creatorUserRole: null,
  editorNetworkState: 'ONLINE',
  permissions: null,
  trialExhaust: null,
  subscription: null,
  bulkDownloadJob: null,
}

// default values are added in the component
const AuthContext = createContext<TourContextType>({
  ...initialState,
  setPendingSidebarCountKeys: (
    _: SidebarCountKeys[],
    __?: 'push' | 'remove'
  ) => {
    /** Handle updating sidebar count data */
  },
  setTopbarComponent: () => {
    /** set topbar component */
  },
  setOpportunityBoardComponent: () => {
    /** set navbar component */
  },
  setNavbarComponent: () => {
    /** set navbar component */
  },
  setSidebarCountData: (_: number, __: SidebarCountKeys) => {
    /** set sidebar count data*/
  },
  setInboxThreadUpdate: () => {
    /** set inbox unread thread count */
  },
  setAssignmentRefetchFunction: () => {},
  setRefetchBoolean: () => {
    /** set Refetch boolean */
  },
  setViewport: () => {},
  setPepperEditorType: () => {},
  /** sets type of pepper editor opened */
  setIsPepperDocEditable: () => {},
  /** sets whether editor is editable */
  setDocErrorUpdate: () => {
    /** sets whether there is error */
  },
  setPrivateItemDetails: () => {
    /** sets details of the private document */
  },
  setEditorUpdateStatus: () => {
    /** sets whether editor is updating or updated */
  },
  setAssignmentDetails: () => {
    // sets details of pepper assignment
  },
  setShouldClearTimeout: () => {
    // sets when to clear time out
  },
  setShouldShowReworkRejectMenu: () => {
    // sets whether to show rework reject menu
  },
  setCreatorUserRole: () => {
    // sets details of the creator role
  },
  setEditorNetworkState: () => {
    // sets network status
  },
  setPermissions: () => {},
  setTrialExhaust: () => {},
  setSubscription: () => {},
  setBulkDownloadJob: () => {},
  setDomainId: () => {},
})

const reducer = (
  state: State,
  action: { type: ReducerType; payload: any }
): State => {
  switch (action.type) {
    case 'SET_PENDING_SIDEBAR_DATA_KEYS': {
      const { type, value } = action.payload
      const currentKeys = state.pendingSidebarCountKeys
      let newKeys: SidebarCountKeys[] = []
      if (type === 'remove')
        newKeys = currentKeys.filter(key => !value.includes(key))
      else newKeys = [...currentKeys, ...value]
      return {
        ...state,
        pendingSidebarCountKeys: [...new Set(newKeys)],
      }
    }
    case 'SET_TOP_BAR_COMPONENT':
      return {
        ...state,
        topbarComponent: action.payload,
      }
    case 'SET_OPPORTUNITY_BOARD_COMPONENT':
      return {
        ...state,
        opportunityBoardComponent: action.payload,
      }
    case 'SET_NAV_BAR_COMPONENT':
      return {
        ...state,
        navbarComponent: action.payload,
      }
    case 'SET_SIDEBAR_COUNT_DATA':
      return {
        ...state,
        sidebarCounts: {
          ...state.sidebarCounts,
          [action.payload.key]: action.payload.value,
        },
      }
    case 'SET_INBOX_UNREAD_UPDATE':
      return {
        ...state,
        inboxThreadUpdate: action.payload,
      }
    case 'SET_ASSIGNMENT_REFETCH_FUNCTION':
      return {
        ...state,
        assignmentRefetchFunction: action.payload,
      }
    case 'SET_REFETCH_BOOLEAN':
      return {
        ...state,
        isRefetchBoolean: action.payload,
      }
    case 'SET_VIEWPORT':
      return {
        ...state,
        viewport: action.payload,
      }
    case 'SET_IS_PEPPER_EDITOR':
      return {
        ...state,
        pepperEditorType: action.payload,
      }
    case 'SET_IS_PEPPER_DOC_EDITABLE':
      return {
        ...state,
        isPepperDocEditable: action.payload,
      }
    case 'SET_DOC_ERROR_UPDATE':
      return {
        ...state,
        docErrorUpdate: action.payload,
      }
    case 'SET_ITEM_DETAILS':
      return {
        ...state,
        privateItemDetails: action.payload,
      }
    case 'SET_EDITOR_UPDATE_STATUS':
      return {
        ...state,
        ...(action.payload === 'UPDATED' && { docErrorUpdate: false }),
        editorUpdateStatus: action.payload,
      }
    case 'SET_ASSIGNMENT_DETAILS':
      return {
        ...state,
        assignmentDetails: action.payload,
      }
    case 'SET_SHOULD_CLEAR_TIMEOUT':
      return {
        ...state,
        shouldClearTimeout: action.payload,
      }
    case 'SET_SHOW_REWORK_REJECT':
      return {
        ...state,
        shouldShowReworkRejectMenu: action.payload,
      }
    case 'SET_CREATOR_USER_ROLE':
      return {
        ...state,
        creatorUserRole: action.payload,
      }
    case 'SET_EDITOR_NETWORK_STATE':
      return {
        ...state,
        editorNetworkState: action.payload,
      }
    case 'SET_PERMISSIONS':
      return {
        ...state,
        permissions: action.payload,
      }
    case 'SET_TRIAL_EXHAUST':
      return {
        ...state,
        trialExhaust: action.payload,
      }
    case 'SET_SUBSCRIPTION':
      return {
        ...state,
        subscription: action.payload,
      }
    case 'SET_DOMAIN_ID':
      return {
        ...state,
        domainId: action.payload,
      }
    default:
      return state
  }
}

const GlobalStateProvider = ({
  children,
}: {
  children: ReactNode
}): ReactElement => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const [bulkDownloadJob, setBulkDownloadJob] =
    useLocalStorage<BulkDownloadState | null>('bulk-download-job-id', null)

  const setPendingSidebarCountKeys = (
    value: SidebarCountKeys[],
    type: 'push' | 'remove' = 'push'
  ) =>
    dispatch({
      type: 'SET_PENDING_SIDEBAR_DATA_KEYS',
      payload: { type, value },
    })

  const setTopbarComponent = (value: ReactNode | null) =>
    dispatch({ type: 'SET_TOP_BAR_COMPONENT', payload: value })

  const setOpportunityBoardComponent = (value: ReactNode | null) =>
    dispatch({ type: 'SET_OPPORTUNITY_BOARD_COMPONENT', payload: value })

  const setNavbarComponent = (value: ReactNode | null) =>
    dispatch({ type: 'SET_NAV_BAR_COMPONENT', payload: value })

  const setSidebarCountData = (value: number, key: SidebarCountKeys) =>
    dispatch({ type: 'SET_SIDEBAR_COUNT_DATA', payload: { value, key } })

  const setInboxThreadUpdate = (value: boolean) =>
    dispatch({ type: 'SET_INBOX_UNREAD_UPDATE', payload: value })

  const setAssignmentRefetchFunction = (fn: () => void) => {
    dispatch({ type: 'SET_ASSIGNMENT_REFETCH_FUNCTION', payload: fn })
  }
  const setRefetchBoolean = (value: boolean) => {
    dispatch({ type: 'SET_REFETCH_BOOLEAN', payload: value })
  }

  const setViewport = (value: string) => {
    dispatch({ type: 'SET_VIEWPORT', payload: value })
  }
  const setPepperEditorType = useCallback((value: PepperDocTypes | null) => {
    dispatch({ type: 'SET_IS_PEPPER_EDITOR', payload: value })
  }, [])
  const setIsPepperDocEditable = useCallback((value: boolean) => {
    dispatch({ type: 'SET_IS_PEPPER_DOC_EDITABLE', payload: value })
  }, [])
  const setDocErrorUpdate = useCallback((value: boolean) => {
    dispatch({ type: 'SET_DOC_ERROR_UPDATE', payload: value })
  }, [])
  const setPrivateItemDetails = useCallback(
    (value: { title: string; code: number; is_renamed: boolean } | null) => {
      dispatch({ type: 'SET_ITEM_DETAILS', payload: value })
    },
    []
  )
  const setEditorUpdateStatus = useCallback((value: EditorUpdateStatusType) => {
    dispatch({ type: 'SET_EDITOR_UPDATE_STATUS', payload: value })
  }, [])
  const setAssignmentDetails = useCallback((value: Assignment | null) => {
    dispatch({ type: 'SET_ASSIGNMENT_DETAILS', payload: value })
  }, [])
  const setShouldClearTimeout = useCallback((value: boolean) => {
    dispatch({ type: 'SET_SHOULD_CLEAR_TIMEOUT', payload: value })
  }, [])
  const setShouldShowReworkRejectMenu = useCallback((value: boolean) => {
    dispatch({ type: 'SET_SHOW_REWORK_REJECT', payload: value })
  }, [])
  const setCreatorUserRole = useCallback((value: CreatorRole | null) => {
    dispatch({ type: 'SET_CREATOR_USER_ROLE', payload: value })
  }, [])
  const setEditorNetworkState = useCallback((value: NetworkStatusType) => {
    dispatch({ type: 'SET_EDITOR_NETWORK_STATE', payload: value })
  }, [])
  const setPermissions = useCallback((value: Permissions) => {
    dispatch({ type: 'SET_PERMISSIONS', payload: value })
  }, [])
  const setTrialExhaust = useCallback((value: TrialExhaust) => {
    dispatch({ type: 'SET_TRIAL_EXHAUST', payload: value })
  }, [])
  const setSubscription = useCallback((value: Subscription) => {
    dispatch({ type: 'SET_SUBSCRIPTION', payload: value })
  }, [])
  const setDomainId = useCallback((value: string) => {
    dispatch({ type: 'SET_DOMAIN_ID', payload: value })
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        bulkDownloadJob,
        setPendingSidebarCountKeys,
        setTopbarComponent,
        setOpportunityBoardComponent,
        setNavbarComponent,
        setSidebarCountData,
        setInboxThreadUpdate,
        setAssignmentRefetchFunction,
        setRefetchBoolean,
        setViewport,
        setPepperEditorType,
        setIsPepperDocEditable,
        setDocErrorUpdate,
        setPrivateItemDetails,
        setEditorUpdateStatus,
        setAssignmentDetails,
        setShouldClearTimeout,
        setShouldShowReworkRejectMenu,
        setCreatorUserRole,
        setEditorNetworkState,
        setPermissions,
        setTrialExhaust,
        setSubscription,
        setBulkDownloadJob,
        setDomainId,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// hooks for accessing context
const useGlobalState = (): TourContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider')
  }
  return context
}

export { GlobalStateProvider, useGlobalState }
